import React, {
  useCallback,
  useEffect,
  useMemo,
  type ComponentType,
  type ReactNode,
} from 'react';

import { getAdminApiUrl } from '@adsk/acs-sc-web-platform-api';
import { CONTEXT_NAMES } from '@adsk/alloy-helpers';
import {
  getOrCreateGlobalContext,
  useStoreWithValidation,
} from '@adsk/alloy-react-context';
import { noop } from '@adsk/alloy-react-helpers';

import { useCurrentProjectId } from './CurrentProjectIdContext';
import {
  BaseCombinedStore,
  createPaginatedStore,
} from './helpers/createDataModificationStore';
import { warnLegacySetEnabled } from './helpers/warnLegacySetEnabled';
import { Role } from './types';

export type RolesContextValue = {
  data: Role[];
  loading: boolean;
  error?: null | Error;
  retry(): void;
  addRoles(roles: Role[]): void;
  updateRole(role: Role): void;
  removeRoleById(id: Role['id']): void;
  setEnabled?: (enabled: boolean) => void;
};

/**
 * Context for roles list.
 */
const RolesContext = getOrCreateGlobalContext<RolesContextValue>(
  CONTEXT_NAMES.RolesContext,
);

const useRolesStoreWithValidation = () =>
  useStoreWithValidation<BaseCombinedStore<Role>>(CONTEXT_NAMES.RolesContext, {
    hook: 'useRoles',
    provider: 'RolesProvider',
  });

const useRolesStore = () =>
  createPaginatedStore<Role>({
    contextName: CONTEXT_NAMES.RolesContext,
  })();

const useRolesApiUrl = () => {
  const PAGE_LIMIT = 200;

  const projectId = useCurrentProjectId();
  const adminApiUrl = getAdminApiUrl();
  return projectId
    ? `${adminApiUrl}/v1/projects/${projectId}/roles?limit=${PAGE_LIMIT}`
    : null;
};

/** Returns the list of roles for the current project, along with loading/error state and store mutation helpers. Must be used inside `RolesProvider`. */
export const useRoles = () => {
  const url = useRolesApiUrl();
  const {
    entities,
    error,
    loading,
    fetchPage,
    addEntities,
    updateEntity,
    removeEntityById,
    retry,
  } = useRolesStoreWithValidation();

  useEffect(() => {
    fetchPage(url);
  }, [url, fetchPage]);

  return {
    data: entities,
    loading,
    error,
    retry,
    addRoles: addEntities,
    updateRole: updateEntity,
    removeRoleById: removeEntityById,
    setEnabled: noop,
  };
};

export const useRolesProvider = () => {
  const url = useRolesApiUrl();
  const {
    entities,
    error,
    loading,
    addEntities,
    updateEntity,
    removeEntityById,
    retry,
    fetchPage,
  } = useRolesStore();

  // Trigger fetch on setEnabled for legacy provider backwards compatibility
  const legacySetEnabled = useCallback(() => {
    warnLegacySetEnabled(CONTEXT_NAMES.RolesContext);
    fetchPage(url);
  }, [fetchPage, url]);

  return useMemo(
    () => ({
      data: entities,
      loading,
      error,
      retry,
      addRoles: addEntities,
      updateRole: updateEntity,
      removeRoleById: removeEntityById,
      setEnabled: legacySetEnabled,
    }),
    [
      entities,
      loading,
      error,
      retry,
      addEntities,
      updateEntity,
      removeEntityById,
      legacySetEnabled,
    ],
  );
};

type RolesProviderProps = {
  children?: ReactNode;
};

export const RolesProvider: ComponentType<RolesProviderProps> = ({
  children,
}) => {
  const value = useRolesProvider();

  return (
    <RolesContext.Provider value={value}>{children}</RolesContext.Provider>
  );
};

RolesProvider.displayName = 'RolesProvider';
